import { boardSearchQuery } from "@/lib/task-rows"
import { csrfHeaders, handleJson, jsonHeaders } from "@/lib/json-request"
import type { ShotFrameSource } from "@/lib/workspace-api"
import type { CanvasShotTreeEpisode } from "@/types/canvas"
import type {
  TaskAgent,
  TaskAssetCandidate,
  TaskAssetKind,
  TaskAssetLink,
  TaskEpicLink,
  TaskKind,
  TaskCardRow,
  TaskColumnPage,
  TaskComment,
  TaskCommentsPage,
  TaskDescriptionOverwrite,
  TaskDraftRow,
  TaskDescriptionVersion,
  TaskDescriptionVersionSummary,
  TaskMentionEntity,
  TaskMentionPerson,
  TaskPerson,
  TaskMentionSearchRow,
  TaskMentionTaskRef,
  TaskMentions,
  TaskPlacement,
  TaskRow,
  TaskStatus,
} from "@/types/tasks"

type Ok = { ok: boolean }

export type TaskInput = {
  title: string
  description: string
  status: TaskStatus
  assignee_id: number | null
  due_on: string | null
  agent?: TaskAgent
  agent_shot_id?: number | null
  asset_kind?: TaskAssetKind | null
  asset_id?: number | null
  kind?: TaskKind
  epic_id?: number | null
}

const tasksPath = (projectId: number) => `/projects/${projectId}/workspace/tasks`

function withBoardSearch(path: string): string {
  const q = boardSearchQuery()
  return q ? `${path}?${new URLSearchParams({ q }).toString()}` : path
}

export class TaskNotFoundError extends Error {}

export async function fetchShotTasks(projectId: number, shotId: number, signal?: AbortSignal) {
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/tasks`, {
    headers: csrfHeaders(),
    credentials: "same-origin",
    signal,
  })
  return handleJson<Ok & { tasks: TaskCardRow[]; members: TaskPerson[] }>(res)
}

export async function fetchTask(projectId: number, taskId: number, signal?: AbortSignal) {
  const res = await fetch(withBoardSearch(`${tasksPath(projectId)}/${taskId}`), {
    headers: csrfHeaders(),
    credentials: "same-origin",
    signal,
  })
  if (res.status === 404) throw new TaskNotFoundError(`HTTP ${res.status}`)
  return handleJson<Ok & { task: TaskRow }>(res)
}

// draftId — черновик формы: его файлы переходят в задачу, сам он удаляется (docs/task_tracker.md «Черновик новой задачи»).
export async function createTask(projectId: number, task: TaskInput, draftId: number | null = null) {
  const res = await fetch(withBoardSearch(tasksPath(projectId)), {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ task, draft_id: draftId }),
  })
  return handleJson<Ok & { task: TaskRow }>(res)
}

export async function saveTaskDescription(
  projectId: number,
  taskId: number,
  description: string,
  baseVersionId: number | null,
) {
  const res = await fetch(withBoardSearch(`${tasksPath(projectId)}/${taskId}`), {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ task: { description }, description_base_version_id: baseVersionId }),
  })
  return handleJson<Ok & { task: TaskRow; overwrote: TaskDescriptionOverwrite | null }>(res)
}

export async function fetchTaskColumn(columnPath: string, params: URLSearchParams, signal?: AbortSignal) {
  const res = await fetch(`${columnPath}?${params.toString()}`, {
    headers: csrfHeaders(),
    credentials: "same-origin",
    signal,
  })
  return handleJson<Ok & TaskColumnPage>(res)
}

export async function updateTask(projectId: number, taskId: number, task: Partial<TaskInput>) {
  const res = await fetch(withBoardSearch(`${tasksPath(projectId)}/${taskId}`), {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ task }),
  })
  return handleJson<Ok & { task: TaskRow }>(res)
}

export async function moveTask(projectId: number, taskId: number, placement: TaskPlacement) {
  const res = await fetch(withBoardSearch(`${tasksPath(projectId)}/${taskId}/move`), {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(placement),
  })
  return handleJson<Ok & { task: TaskRow }>(res)
}

export async function deleteTask(projectId: number, taskId: number) {
  const res = await fetch(`${tasksPath(projectId)}/${taskId}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<Ok>(res)
}

const taskFilesPath = (projectId: number, taskId: number) => `${tasksPath(projectId)}/${taskId}/files`

type AttachedTaskFile = Ok & { task: TaskRow; blob_id: number }

export async function uploadTaskFile(projectId: number, taskId: number, file: File) {
  const form = new FormData()
  form.append("file", file)
  const res = await fetch(withBoardSearch(taskFilesPath(projectId, taskId)), {
    method: "POST",
    headers: csrfHeaders(),
    credentials: "same-origin",
    body: form,
  })
  return handleJson<AttachedTaskFile>(res)
}

export async function attachTaskFileFromLibrary(projectId: number, taskId: number, source: ShotFrameSource) {
  const res = await fetch(withBoardSearch(taskFilesPath(projectId, taskId)), {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ source }),
  })
  return handleJson<AttachedTaskFile>(res)
}

export async function deleteTaskFile(projectId: number, taskId: number, blobId: number) {
  const res = await fetch(withBoardSearch(`${taskFilesPath(projectId, taskId)}/${blobId}`), {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<Ok & { task: TaskRow }>(res)
}

export type TaskDraftScope = { kind: "project"; projectId: number } | { kind: "company" }

export type TaskDraftInput = TaskInput & { project_id?: number | null }

const taskDraftPath = (scope: TaskDraftScope) =>
  scope.kind === "project" ? `/projects/${scope.projectId}/workspace/task_draft` : "/company/task_draft"

type SavedTaskDraft = Ok & { draft: TaskDraftRow }

export async function saveTaskDraft(scope: TaskDraftScope, draft: TaskDraftInput, { keepalive = false } = {}) {
  const res = await fetch(taskDraftPath(scope), {
    method: "PUT",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ draft }),
    keepalive,
  })
  return handleJson<SavedTaskDraft>(res)
}

export async function deleteTaskDraft(scope: TaskDraftScope) {
  const res = await fetch(taskDraftPath(scope), {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<Ok>(res)
}

export async function uploadTaskDraftFile(scope: TaskDraftScope, file: File) {
  const form = new FormData()
  form.append("file", file)
  const res = await fetch(`${taskDraftPath(scope)}/files`, {
    method: "POST",
    headers: csrfHeaders(),
    credentials: "same-origin",
    body: form,
  })
  return handleJson<SavedTaskDraft & { blob_id: number }>(res)
}

export async function attachTaskDraftFileFromLibrary(scope: TaskDraftScope, source: ShotFrameSource) {
  const res = await fetch(`${taskDraftPath(scope)}/files`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ source }),
  })
  return handleJson<SavedTaskDraft & { blob_id: number }>(res)
}

export async function deleteTaskDraftFile(scope: TaskDraftScope, blobId: number) {
  const res = await fetch(`${taskDraftPath(scope)}/files/${blobId}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<SavedTaskDraft>(res)
}

const taskCommentsPath = (projectId: number, taskId: number) => `${tasksPath(projectId)}/${taskId}/comments`

export type TaskCommentsQuery = { beforeId?: number; sinceId?: number }

export async function fetchTaskComments(
  projectId: number,
  taskId: number,
  query: TaskCommentsQuery = {},
  signal?: AbortSignal,
) {
  const params = new URLSearchParams()
  if (query.beforeId != null) params.set("before_id", String(query.beforeId))
  if (query.sinceId != null) params.set("since_id", String(query.sinceId))
  const search = params.toString()
  const res = await fetch(`${taskCommentsPath(projectId, taskId)}${search ? `?${search}` : ""}`, {
    headers: csrfHeaders(),
    credentials: "same-origin",
    signal,
  })
  return handleJson<Ok & TaskCommentsPage>(res)
}

export async function createTaskComment(projectId: number, taskId: number, body: string) {
  const res = await fetch(taskCommentsPath(projectId, taskId), {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ comment: { body } }),
  })
  return handleJson<Ok & { comment: TaskComment; mentions: TaskMentions }>(res)
}

export async function updateTaskComment(projectId: number, taskId: number, commentId: number, body: string) {
  const res = await fetch(`${taskCommentsPath(projectId, taskId)}/${commentId}`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ comment: { body } }),
  })
  return handleJson<Ok & { comment: TaskComment; mentions: TaskMentions }>(res)
}

export async function deleteTaskComment(projectId: number, taskId: number, commentId: number) {
  const res = await fetch(`${taskCommentsPath(projectId, taskId)}/${commentId}`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<Ok>(res)
}

const taskDescriptionVersionsPath = (projectId: number, taskId: number) =>
  `${tasksPath(projectId)}/${taskId}/description_versions`

export async function fetchTaskDescriptionVersions(projectId: number, taskId: number, signal?: AbortSignal) {
  const res = await fetch(taskDescriptionVersionsPath(projectId, taskId), {
    headers: csrfHeaders(),
    credentials: "same-origin",
    signal,
  })
  return handleJson<Ok & { versions: TaskDescriptionVersionSummary[] }>(res)
}

type TaskDescriptionVersionPage = Ok & {
  version: TaskDescriptionVersion
  previous: { id: number; description: string } | null
  mentions: TaskMentions
}

export async function fetchTaskDescriptionVersion(
  projectId: number,
  taskId: number,
  versionId: number,
  signal?: AbortSignal,
) {
  const res = await fetch(`${taskDescriptionVersionsPath(projectId, taskId)}/${versionId}`, {
    headers: csrfHeaders(),
    credentials: "same-origin",
    signal,
  })
  return handleJson<TaskDescriptionVersionPage>(res)
}

export async function restoreTaskDescriptionVersion(projectId: number, taskId: number, versionId: number) {
  const res = await fetch(withBoardSearch(`${taskDescriptionVersionsPath(projectId, taskId)}/${versionId}/restore`), {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
  })
  return handleJson<Ok & { task: TaskRow }>(res)
}

const taskMentionsPath = "/company/task_mentions"

export async function fetchTaskMentionPeople(projectId: number, signal?: AbortSignal) {
  const params = new URLSearchParams({ project_id: String(projectId) })
  const res = await fetch(`${taskMentionsPath}/people?${params.toString()}`, {
    headers: csrfHeaders(),
    credentials: "same-origin",
    signal,
  })
  return handleJson<Ok & { people: TaskMentionPerson[] }>(res)
}

export async function fetchTaskMentionEntities(projectId: number, signal?: AbortSignal) {
  const params = new URLSearchParams({ project_id: String(projectId) })
  const res = await fetch(`${taskMentionsPath}/entities?${params.toString()}`, {
    headers: csrfHeaders(),
    credentials: "same-origin",
    signal,
  })
  return handleJson<Ok & { entities?: TaskMentionEntity[] }>(res)
}

export async function searchTaskMentionTasks(
  query: { q: string; excludeTaskId: number | null; projectId: number | null },
  signal?: AbortSignal,
) {
  const params = new URLSearchParams({ q: query.q })
  if (query.excludeTaskId != null) params.set("exclude_task_id", String(query.excludeTaskId))
  if (query.projectId != null) params.set("project_id", String(query.projectId))
  const res = await fetch(`${taskMentionsPath}/tasks?${params.toString()}`, {
    headers: csrfHeaders(),
    credentials: "same-origin",
    signal,
  })
  return handleJson<Ok & { tasks: TaskMentionSearchRow[] }>(res)
}

export async function resolveTaskMentionTasks(ids: number[], signal?: AbortSignal) {
  const res = await fetch(`${taskMentionsPath}/tasks/resolve?ids=${ids.join(",")}`, {
    headers: csrfHeaders(),
    credentials: "same-origin",
    signal,
  })
  return handleJson<Ok & { tasks: TaskMentionTaskRef[] }>(res)
}

export async function fetchTaskEpics(projectId: number, signal?: AbortSignal) {
  const res = await fetch(`${tasksPath(projectId)}/epics`, {
    headers: csrfHeaders(),
    credentials: "same-origin",
    signal,
  })
  const data = await handleJson<Ok & { epics: TaskEpicLink[] }>(res)
  return data.epics
}

export async function fetchAssetTaskCandidates(projectId: number, kind: TaskAssetKind, signal?: AbortSignal) {
  const params = new URLSearchParams({ kind })
  const res = await fetch(`${tasksPath(projectId)}/asset_candidates?${params.toString()}`, {
    headers: csrfHeaders(),
    credentials: "same-origin",
    signal,
  })
  const data = await handleJson<Ok & { tasks: TaskAssetCandidate[] }>(res)
  return data.tasks
}

export async function fetchAssetTaskOptions(projectId: number, kind: TaskAssetKind, signal?: AbortSignal) {
  const params = new URLSearchParams({ kind })
  const res = await fetch(`${tasksPath(projectId)}/asset_options?${params.toString()}`, {
    headers: csrfHeaders(),
    credentials: "same-origin",
    signal,
  })
  const data = await handleJson<Ok & { options: TaskAssetLink[] }>(res)
  return data.options
}

// Передача задачи агенту (docs/task_agent_delegation_plan.md).

export async function fetchTaskAgentShots(projectId: number) {
  const res = await fetch(`${tasksPath(projectId)}/agent_shots`, {
    headers: csrfHeaders(),
    credentials: "same-origin",
  })
  return handleJson<Ok & { shot_tree: CanvasShotTreeEpisode[] }>(res)
}

export type TaskAgentBrief = { prompt: string; decisions_source: "llm" | "fallback" | "none" }

export async function prepareTaskAgentBrief(projectId: number, taskId: number, signal?: AbortSignal) {
  const res = await fetch(`${tasksPath(projectId)}/${taskId}/agent_brief`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    signal,
  })
  return handleJson<Ok & TaskAgentBrief>(res)
}

// comment — null, если этот запрос уже был отправлен (повторный клик).
export async function delegateTaskToAgent(projectId: number, taskId: number, prompt: string, requestId: string) {
  const res = await fetch(`${tasksPath(projectId)}/${taskId}/delegate`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "same-origin",
    body: JSON.stringify({ prompt, request_id: requestId }),
  })
  return handleJson<Ok & { comment: TaskComment | null; mentions: TaskMentions | null }>(res)
}

/** Панель задачи вне доски — в ленте студии (docs/studio_feed.md). */
export type TaskPanelContext = {
  members: TaskPerson[]
  frame_format: string
  project_name: string
  can_comment: boolean
  can_view_team: boolean
}

export async function fetchTaskPanelContext(projectId: number, signal?: AbortSignal) {
  const res = await fetch(`${tasksPath(projectId)}/panel_context`, {
    headers: csrfHeaders(),
    credentials: "same-origin",
    signal,
  })
  return handleJson<Ok & TaskPanelContext>(res)
}
